import { BAKERY_HOURS } from "@/lib/constants/site"

type KitchenHoursNoticeProps = {
  /** "light" for cream/blush backgrounds; "dark" for the espresso (#5F5156) bar + footer. */
  tone?: "light" | "dark"
  /** "short" pill copy (default) or the fuller two-sentence version. */
  format?: "short" | "long"
  className?: string
}

/**
 * Tasteful, non-alarming reminder that the hot kitchen stops taking orders at
 * 2 PM while bakery items and drinks stay available until the café closes at
 * 3:30 PM. The copy is the single source of truth in BAKERY_HOURS so it reads
 * identically everywhere it appears.
 *
 * Deliberately NOT an alert: no role="alert"/live region and no warning colors —
 * it's an informational chip in brand tones, legible down to mobile widths.
 */
export function KitchenHoursNotice({
  tone = "light",
  format = "short",
  className = "",
}: KitchenHoursNoticeProps) {
  const text =
    format === "long"
      ? BAKERY_HOURS.kitchenNoticeLong
      : BAKERY_HOURS.kitchenNotice

  const toneClasses =
    tone === "dark"
      ? "border-white/25 bg-white/10 text-[#FFF9F8]"
      : "border-[#F5928A]/45 bg-[#FBD1C8]/30 text-[#5F5156]"
  const iconClasses = tone === "dark" ? "text-[#F5928A]" : "text-[#A8473C]"

  return (
    <span
      className={`inline-flex max-w-full items-start gap-2 rounded-2xl border px-3.5 py-2 font-body text-xs font-medium leading-snug sm:text-[13px] ${toneClasses} ${className}`}
    >
      <svg
        className={`mt-[1.5px] h-3.5 w-3.5 shrink-0 ${iconClasses}`}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.8}
          d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
      <span>{text}</span>
    </span>
  )
}

export default KitchenHoursNotice
